import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "../components/ui/sheet";
import Logo from "./Logo";
import { companyInfo } from "../data/mockData";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "About", path: "/about" },
  { name: "Testimonials", path: "/testimonials" },
  { name: "Resources", path: "/blog" },
  { name: "Contact", path: "/contact" }
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      data-testid="main-navbar"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-stone-50/95 backdrop-blur-md shadow-sm"
          : "bg-stone-50/70 backdrop-blur-sm"
      } border-b border-stone-200/60`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Logo size="default" />

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`elegant-link text-sm font-medium tracking-wide ${
                  location.pathname === link.path
                    ? "text-amber-700"
                    : "text-slate-800 hover:text-amber-700"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`tel:${companyInfo.phone.replace(/\D/g, "")}`}
              data-testid="nav-phone-link"
              className="flex items-center gap-2 text-slate-800 hover:text-amber-700 transition-colors text-sm font-semibold"
            >
              <Phone className="w-4 h-4" />
              {companyInfo.phone}
            </a>
            <Link to="/contact">
              <Button
                data-testid="nav-cta-button"
                className="bg-amber-700 hover:bg-amber-800 text-white rounded-full px-6 py-5 font-medium tracking-wide"
              >
                Request Care
              </Button>
            </Link>
          </div>

          {/* Mobile */}
          <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="mobile-menu-toggle"
                  className="text-slate-900 p-2"
                  aria-label="Open menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-stone-50 w-[320px] sm:w-[400px]">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <SheetDescription className="sr-only">Main site navigation links</SheetDescription>
                <div className="flex flex-col h-full pt-6">
                  <div className="mb-10">
                    <Logo size="default" />
                  </div>
                  <div className="flex flex-col gap-5">
                    {navLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                        className={`font-serif text-2xl ${
                          location.pathname === link.path
                            ? "text-amber-700"
                            : "text-slate-900"
                        } hover:text-amber-700 transition-colors`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                  <div className="mt-auto mb-8 pt-10 border-t border-stone-200">
                    <a
                      href={`tel:${companyInfo.phone.replace(/\D/g, "")}`}
                      className="flex items-center gap-3 text-slate-900 font-semibold mb-4"
                    >
                      <Phone className="w-5 h-5 text-amber-700" />
                      {companyInfo.phone}
                    </a>
                    <Link to="/contact" onClick={() => setOpen(false)}>
                      <Button
                        data-testid="mobile-cta-button"
                        className="w-full bg-amber-700 hover:bg-amber-800 text-white rounded-full py-6"
                      >
                        Request Care
                      </Button>
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
